import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  HStack,
  Link,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { Elements } from "@stripe/react-stripe-js";
import React, { useContext, useMemo } from "react";

import { EquidefiMarkdown } from "@equidefi/portals/components/Markdown";
import { computeFeeBreakdown, offeringToFeeRates } from "@equidefi/shared";
import { BannerAlert, Text } from "@equidefi/ui";
import { Icon } from "@equidefi/ui/icon";

import StripePaymentForm from "@/components/StripePaymentForm";
import { WorkflowButton } from "@/components/workflow/WorkflowButton";
import { CurrencyFormatter } from "@/constants/format";
import { VAULT_ROUTES } from "@/constants/routes";
import { APPEARANCE } from "@equidefi/constants/stripe";

import { usePlatformSettings } from "@/hooks/useOfferings";
import { usePaymentAvailability } from "@/hooks/usePaymentAvailability";
import { STRIPE_ACCORDION_TYPES } from "@/pages/app/vault/VaultPayment";
import { useWorkflowContext } from "@/pages/workflow/context/WorkflowContext";
import CryptoPayment from "@/pages/workflow/payment/CryptoPayment";
import { ProcessingFeeSummary } from "./ProcessingFeeSummary";
import { StripeWireSection } from "./StripeWireSection";
import { PaymentStepContext } from "./context";

const PaymentMessage = ({ paymentMethod }) => {
  const { settings, offering } = useWorkflowContext();

  if (settings?.ach_disabled === true && paymentMethod === "us_bank_account") {
    return (
      <BannerAlert
        status="error"
        title="ACH Payments"
        description={settings?.ach_message}
        titleLayout="inline"
        mb={4}
      />
    );
  }

  let description =
    "When paying by credit card, all fees are covered by the Company.  A temporary payment authorization will be placed on your card.  Your card will only be charged once your investment in accepted.";

  if (offering?.settings?.investor_pays_fee) {
    description =
      "When paying by card or ACH, the processing fee is added to your total.  A temporary payment authorization will be placed on your card.  Your card will only be charged once your investment is accepted.";
  }
  return (
    <BannerAlert status="info" title="Credit Card Payments" description={description} titleLayout="inline" mb={4} />
  );
};

const AccordionSection = React.forwardRef(
  ({ title, description, feeText, children, alertText, hidden, onSelect, panelProps = {}, ...rest }, ref) => {
    if (hidden) return null;

    return (
      <AccordionItem {...rest} ref={ref} my="2" w="full">
        {({ isExpanded }) => {
          return (
            <Box borderRadius="lg" py={2} pb={isExpanded ? 0 : 2} border={isExpanded ? "1px solid" : null}>
              <AccordionButton onClick={onSelect}>
                <input type="radio" style={{ marginRight: 5, width: "16px", height: "16px" }} checked={isExpanded} />
                <VStack align="start" spacing={0} flex={1} ml={2}>
                  <Text fontWeight="bold" m="0">
                    {title}
                  </Text>
                  {description && (
                    <Text textStyle="body2" color="gray.600" textAlign="left" m="0">
                      {description}
                    </Text>
                  )}
                  {feeText && (
                    <Text textStyle="body2" color="gray.500" m="0">
                      {feeText}
                    </Text>
                  )}
                </VStack>
                <AccordionIcon ml="auto" />
              </AccordionButton>
              <AccordionPanel {...panelProps}>
                {alertText && <BannerAlert status="warning" description={alertText} mb={4} />}
                {children}
              </AccordionPanel>
            </Box>
          );
        }}
      </AccordionItem>
    );
  },
);

export const PaymentAccordion = ({
  onSubmitManualPayment = () => {},
  isManualPaymentLoading = false,
  supportsStripePayment,
  onSectionChange,
  onWireContinue = () => {},
  wireInstructions = null,
  isWireLoading = false,
  isWireContinueLoading = false,
  paymentSettings,
  stripe,
  investment,
  offering,
  onStripeMethodChange = () => {},
  deferredIntent = false,
  refetchInvestment,
  analytics,
  redirectUrl = `/offerings/${offering?.slug}/${investment?.id}/payment/processing`,
}) => {
  const platformSettings = usePlatformSettings();
  const paymentAvailability = usePaymentAvailability({
    offering,
    investmentAmount: investment?.amount,
    platformSettings: platformSettings?.data,
  });
  const { clientSecret, paymentIntentId } = useContext(PaymentStepContext);
  const [paymentMethod, setPaymentMethod] = React.useState(null);

  const investorPaysFee = Boolean(offering?.settings?.investor_pays_fee);
  const feeRates = useMemo(() => offeringToFeeRates(offering), [offering]);

  // "Processing fee: $X" hint shown under a method.
  const feeText = (type) => {
    if (!investorPaysFee) return null;
    const { investorFeeTotal } = computeFeeBreakdown(feeRates, type, Number(investment?.amount ?? 0));
    return `Processing fee: ${CurrencyFormatter.format(investorFeeTotal)}`;
  };

  // Lift the Stripe sub-type (card vs us_bank_account) up so the summary fee updates.
  const handleStripeMethodChange = (type) => {
    setPaymentMethod(type);
    onStripeMethodChange(type);
  };

  // Partially paid investments settle the remainder, so Elements has to mount on
  // what is still owed rather than on the original investment amount.
  const dueAmount = Math.max(Number(investment?.amount ?? 0) - Number(investment?.paid_amount ?? 0), 0);

  // Same helper the server prices the intent with, so the mounted amount and the
  // created intent agree without a round trip.
  const stripeAmountInCents = useMemo(
    () => Math.round(computeFeeBreakdown(feeRates, paymentMethod ?? "card", dueAmount).chargeAmount * 100),
    [feeRates, paymentMethod, dueAmount],
  );

  const options = useMemo(
    () =>
      deferredIntent
        ? {
            mode: "payment",
            currency: "usd",
            amount: stripeAmountInCents,
            setupFutureUsage: "off_session",
            appearance: APPEARANCE,
          }
        : { clientSecret, appearance: APPEARANCE },
    [deferredIntent, stripeAmountInCents, clientSecret],
  );

  // Deferred mounts as soon as Stripe.js resolves; the legacy flow has to wait for
  // the intent the accordion just created.
  const canMountStripe = deferredIntent ? Boolean(stripe) && stripeAmountInCents > 0 : Boolean(clientSecret);

  return (
    <Accordion variant="card" allowToggle>
      <AccordionSection
        title="Crypto Payments"
        description="Accepting BTC, ETH, SOL, and more"
        feeText={feeText("crypto")}
        onSelect={() => onSectionChange("CRYPTO")}
        hidden={!offering?.settings?.crypto_enabled}>
        <CryptoPayment
          investment={investment}
          offering={offering}
          refetchInvestment={refetchInvestment}
          analytics={analytics}
          redirectUrl={`${redirectUrl}?crypto=true`}
        />
      </AccordionSection>

      {paymentAvailability.isStripeAvailable && (
        <Tooltip label={paymentAvailability.stripeDisabledMessage} hasArrow openDelay={250}>
          <AccordionSection
            title="Credit Card or ACH"
            description={investorPaysFee ? "Pay with credit card or ACH" : "The company pays the transaction fee."}
            // Reflect the chosen Stripe sub-type (card vs ACH have different rates);
            // default to the card rate until the investor picks a method.
            feeText={feeText(paymentMethod ?? "card")}
            isDisabled={paymentAvailability.isOverAnyLimit}
            onSelect={() => onSectionChange(STRIPE_ACCORDION_TYPES.STRIPE)}
            panelProps={{ minHeight: "200px" }}>
            <PaymentMessage paymentMethod={paymentMethod} />
            <Text textStyle="body2" m="0">
              {canMountStripe && (
                <Elements stripe={stripe} options={options}>
                  <StripePaymentForm
                    investment={investment}
                    paymentIntentId={paymentIntentId}
                    returnPath={redirectUrl}
                    offering={offering}
                    setPaymentMethod={handleStripeMethodChange}
                    deferredIntent={deferredIntent}
                    amountInCents={stripeAmountInCents}
                  />
                </Elements>
              )}
            </Text>
          </AccordionSection>
        </Tooltip>
      )}

      {paymentAvailability.isStripeWireAvailable && (
        <AccordionSection
          title="Wire Transfer"
          description="Pay via bank wire transfer. Wire instructions will be provided."
          feeText={feeText("customer_balance")}
          onSelect={() => onSectionChange(STRIPE_ACCORDION_TYPES.STRIPE_WIRE)}
          panelProps={{ minHeight: "200px" }}
          alertText="It may take 1-3 business days for the wire to arrive.">
          <StripeWireSection
            investment={investment}
            offering={offering}
            wireInstructions={wireInstructions}
            isLoading={isWireLoading}
            isContinueLoading={isWireContinueLoading}
            onContinue={onWireContinue}
          />
        </AccordionSection>
      )}

      <AccordionSection
        title="Wire Transfer"
        description="Bank Wire Payments."
        feeText={feeText("wire")}
        hidden={!paymentAvailability.isWireAvailable}
        alertText="It may take 1 business day to complete the transaction."
        onSelect={() => onSectionChange("WIRE")}>
        <Text fontWeight="bold" textStyle="body">
          Please initiate your wire to the account listed below:
        </Text>

        {paymentSettings.wireInstructions?.length > 0 && (
          <EquidefiMarkdown textStyle="body" my={4}>
            {paymentSettings.wireInstructions}
          </EquidefiMarkdown>
        )}

        <HStack spacing={3} mt={2} mb={3}>
          <Link
            href={VAULT_ROUTES.printPayments(investment?.id, "wire")}
            target="_blank"
            rel="noreferrer"
            display="inline-flex"
            alignItems="center"
            gap={2}
            pb={2}
            color="blue.600"
            fontWeight="medium">
            <Icon.Printer size="1.2em" />
            Print Payment Instructions
          </Link>
        </HStack>

        <ProcessingFeeSummary investment={investment} offering={offering} paymentType="wire" />

        <WorkflowButton isLoading={isManualPaymentLoading} onClick={() => onSubmitManualPayment()}>
          Continue
        </WorkflowButton>
      </AccordionSection>

      <AccordionSection
        type="CHECK"
        title="Check"
        description="Send a check by mail."
        feeText={feeText("check")}
        hidden={!paymentAvailability.isCheckAvailable}
        alertText="It may take several days to complete the transaction."
        onSelect={() => onSectionChange("CHECK")}>
        <Text fontWeight="bold" textStyle="body">
          Include the investment amount and send your check to:
        </Text>

        {paymentSettings.checkInstructions?.length > 0 && (
          <EquidefiMarkdown textStyle="body" my={4}>
            {paymentSettings.checkInstructions}
          </EquidefiMarkdown>
        )}

        <HStack spacing={3} mt={2} mb={3}>
          <Link
            href={VAULT_ROUTES.printPayments(investment?.id, "check")}
            target="_blank"
            rel="noreferrer"
            display="inline-flex"
            alignItems="center"
            gap={2}
            pb={2}
            color="blue.600"
            fontWeight="medium">
            <Icon.Printer size="1.2em" />
            Print Payment Instructions
          </Link>
        </HStack>

        <ProcessingFeeSummary investment={investment} offering={offering} paymentType="check" />

        <WorkflowButton isLoading={isManualPaymentLoading} onClick={() => onSubmitManualPayment()}>
          Continue
        </WorkflowButton>
      </AccordionSection>

      {paymentAvailability.stripeDisabledMessage && (
        <BannerAlert status="info" description={paymentAvailability.stripeDisabledMessage} my={4} />
      )}
    </Accordion>
  );
};
